import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [eventDays, setEventDays] = useState(15);
  const [events, setEvents] = useState([]);

  // Action-center summary (one round trip; role-trimmed server-side).
  useEffect(() => {
    let active = true;
    apiFetch('/api/dashboard/summary')
      .then((res) => (res.ok ? res.json() : null))
      .then((d) => { if (active && d) setSummary(d.summary || null); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // Cross-band upcoming events (cached). Visible to all roles.
  useEffect(() => {
    let active = true;
    apiFetch(`/api/clients/events/upcoming?days=${eventDays}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((d) => { if (active && d) setEvents(d.events || []); })
      .catch(() => {});
    return () => { active = false; };
  }, [eventDays]);

  const isManager = !!(summary && summary.is_manager);

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Dashboard</h1>
        <p>Welcome back{user && user.first_name ? `, ${user.first_name}` : ''}.</p>
      </div>

      {summary && (
        <div className="hq-dash-grid">
          <button className="hq-dash-card hq-dash-alert" onClick={() => navigate('/packages')}>
            <div className="hq-dash-card-head">
              <span className="hq-dash-card-title">Packages</span>
              <span className="hq-dash-arrow">→</span>
            </div>
            <div className="hq-dash-rows">
              <div className="hq-dash-row hq-dash-row-alert"><span>Exceptions</span><strong>{summary.packages.exception}</strong></div>
              <div className="hq-dash-row"><span>Out for delivery</span><strong>{summary.packages.out_for_delivery}</strong></div>
              <div className="hq-dash-row"><span>Ready for pickup</span><strong>{summary.packages.ready_for_pickup}</strong></div>
            </div>
          </button>

          <button className="hq-dash-card" onClick={() => navigate('/production')}>
            <div className="hq-dash-card-head">
              <span className="hq-dash-card-title">Production</span>
              <span className="hq-dash-arrow">→</span>
            </div>
            <div className="hq-dash-rows hq-dash-row-active">
              <div className="hq-dash-row hq-dash-row-warn"><span>Ships this week</span><strong>{summary.production.ships_this_week}</strong></div>
              <div className="hq-dash-row"><span>Ready to ship</span><strong>{summary.production.ready_to_ship}</strong></div>
              <div className="hq-dash-row"><span>Active jobs</span><strong>{summary.production.active}</strong></div>
            </div>
          </button>

          <button className="hq-dash-card" onClick={() => navigate('/production')}>
            <div className="hq-dash-card-head">
              <span className="hq-dash-card-title">Inbound blanks</span>
              <span className="hq-dash-arrow">→</span>
            </div>
            <div className="hq-dash-rows">
              <div className="hq-dash-row"><span>In transit</span><strong>{summary.blanks.active}</strong></div>
            </div>
          </button>

          {summary.shipstation && summary.shipstation.configured && (
            <div className="hq-dash-card" style={{ cursor: 'default' }}>
              {/* @@SHIPSTATION_SUMMARY_v1@@ */}
              <div className="hq-dash-card-head">
                <span className="hq-dash-card-title"><a href="https://ship.shipstation.com" target="_blank" rel="noreferrer" style={{ color: '#31b44b', textDecoration: 'none' }}>ShipStation ↗</a></span>{/* @@CARD_EXTLINKS_v1@@ */}
              </div>
              <div className="hq-dash-rows">
                <div className="hq-dash-row hq-dash-row-warn"><span>On hold</span><strong>{summary.shipstation.on_hold ?? '—'}</strong></div>
                <div className="hq-dash-row hq-dash-row-alert"><span>Awaiting shipment</span><strong>{summary.shipstation.awaiting_shipment ?? '—'}</strong></div>
                <div className="hq-dash-row"><span>Shipped MTD</span><strong>{summary.shipstation.mtd_shipments ?? '—'}</strong></div>
                <div className="hq-dash-row hq-dash-row-sub"><span>Shipped YTD</span><strong>{summary.shipstation.ytd_shipments ?? '—'}</strong></div>
              </div>
            </div>
          )}

          {isManager && summary.purchase_orders && (
            <button className="hq-dash-card" onClick={() => navigate('/purchase-orders')}>
              <div className="hq-dash-card-head">
                <span className="hq-dash-card-title">Purchase orders</span>
                <span className="hq-dash-arrow">→</span>
              </div>
              <div className="hq-dash-rows">
                <div className="hq-dash-row"><span>Open</span><strong>{summary.purchase_orders.open}</strong></div>
                <div className="hq-dash-row"><span>Total</span><strong>{summary.purchase_orders.total}</strong></div>
                <div className="hq-dash-row hq-dash-row-sub"><span>Vendors cached</span><strong>{summary.vendors ? summary.vendors.total : 0}</strong></div>
              </div>
            </button>
          )}

          {isManager && summary.leads && (
            <button className="hq-dash-card" onClick={() => navigate('/leads')}>
              <div className="hq-dash-card-head">
                <span className="hq-dash-card-title">Leads</span>
                <span className="hq-dash-arrow">→</span>
              </div>
              <div className="hq-dash-rows">
                <div className="hq-dash-row"><span>New (uncontacted)</span><strong>{summary.leads.new}</strong></div>
                <div className="hq-dash-row"><span>Opened, not followed up</span><strong>{summary.leads.opened_no_followup}</strong></div>
              </div>
            </button>
          )}

          {summary.support && summary.support.configured && (
            <div className="hq-dash-card" style={{ cursor: 'default' }}>
              {/* @@FRESHDESK_SUPPORT_CARD_v1@@ */}
              <div className="hq-dash-card-head">
                <span className="hq-dash-card-title"><a href="https://support.xyzmerch.com" target="_blank" rel="noreferrer" style={{ color: '#31b44b', textDecoration: 'none' }}>Support tickets ↗</a></span>
              </div>
              <div className="hq-dash-rows">
                <div className="hq-dash-row hq-dash-row-alert"><span>Open</span><strong>{summary.support.counts.open ?? '—'}</strong></div>
                <div className="hq-dash-row hq-dash-row-warn"><span>Pending</span><strong>{summary.support.counts.pending ?? '—'}</strong></div>
                <div className="hq-dash-row"><span>Resolved</span><strong>{summary.support.counts.resolved ?? '—'}</strong></div>
              </div>
            </div>
          )}

          <button className="hq-dash-card" onClick={() => navigate('/timeclock')} disabled={!isManager} style={!isManager ? { cursor: 'default' } : undefined}>
            <div className="hq-dash-card-head">
              <span className="hq-dash-card-title">Time clock</span>
              {isManager && <span className="hq-dash-arrow">→</span>}
            </div>
            <div className="hq-dash-rows">
              <div className="hq-dash-row hq-dash-row-ok"><span>On duty now</span><strong>{summary.timeclock.on_duty_now}</strong></div>
            </div>
          </button>
        </div>
      )}
      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Upcoming events</h2>
          <div style={{ display: 'flex', gap: 6 }}>
            {[15, 30, 60].map((d) => (
              <button key={d}
                className={d === eventDays ? 'hq-btn' : 'hq-signout'}
                style={{ width: 'auto', padding: '0 14px', height: 38 }}
                onClick={() => setEventDays(d)}>
                {d}d
              </button>
            ))}
          </div>
        </div>
        <table className="hq-table">
          <thead><tr><th>Date</th><th>Band</th><th>Event</th><th>Venue</th><th>Location</th></tr></thead>
          <tbody>
            {events.map((ev) => (
              <tr key={ev.id}>
                <td>{ev.starts_at ? new Date(ev.starts_at).toLocaleDateString() : '—'}</td>
                <td>{ev.client_name || '—'}</td>
                <td>
                  {ev.event_url
                    ? <a href={ev.event_url} target="_blank" rel="noreferrer" style={{ color: '#31b44b', textDecoration: 'none' }}>{ev.title || 'Event'}</a>
                    : (ev.title || '—')}
                </td>
                <td>{ev.venue_name || '—'}</td>
                <td style={{ color: 'var(--hq-muted)' }}>{[ev.city, ev.region, ev.country].filter(Boolean).join(', ') || '—'}</td>
              </tr>
            ))}
            {events.length === 0 && (
              <tr><td className="hq-empty" colSpan={5}>No events in the next {eventDays} days.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="hq-panel">
        <div className="hq-panel-head"><h2>Your account</h2></div>
        <table className="hq-table">
          <tbody>
            <tr><td className="hq-role">Name</td><td>{user ? `${user.first_name} ${user.last_name}`.trim() || '—' : '—'}</td></tr>
            <tr><td className="hq-role">Email</td><td>{user && user.email}</td></tr>
            <tr><td className="hq-role">Role</td><td style={{ textTransform: 'capitalize' }}>{user && user.role}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
